import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Outlet, useLocation, useParams } from 'react-router-dom';
import { Box, Stack, Tab, Tabs, Typography, useTheme } from '@mui/material';
import { match } from 'path-to-regexp';
import { useQueryParams } from 'use-query-params';

import {
  FolderType,
  GetOrganizationItemsLocationType,
  useGetOrganizationBySlug,
  useGetOrganizationStructure
} from '@/api/generated';
import CachedLocationDisplay from '@/components/fields/Location/CachedLocationDisplay';
import LocationNavSelect from '@/components/fields/Location/LocationNavSelect';
import { buildFlatStructure } from '@/components/fields/Location/locationTreeUtils';
import StickyHead from '@/components/StickyHead/StickyHead';
import { LocationProvider } from '@/hooks/entities/useLocation';
import { PermissionKeys, useAnyPermissions } from '@/hooks/permissions/usePermissions';
import { useSubfolderPermissions } from '@/hooks/permissions/useSubfolderPermissions';
import { OrganizationStructureProvider, useOrganizationStructure } from '@/hooks/useOrganizationStructure';
import { usePageTitle } from '@/hooks/usePageTitle';
import {
  ROUTER_PATHS,
  SETTINGS_STEP_KEYS,
  toOrganizationItems,
  toOrganizationItemSettings,
  toOrganizationProcesses,
  toOrganizationSettings,
  toOrganizationTalents
} from '@/root/linker';
import { borders } from '@/theme/constants';
import typography from '@/theme/typography';

import { URL_CONFIG_MAP } from './defaults';
import OrganizationInfo from './OrganizationInfo';

const TALENT_ENABLED_ITEM_TYPES: FolderType[] = [FolderType.FOLDER];
const PROCESS_ENABLED_ITEM_TYPES: FolderType[] = [FolderType.FOLDER, FolderType.SUB_FOLDER];

const Organization = () => {
  const { organizationSlug } = useParams() as {
    organizationSlug: string;
  };
  const [{ locationSlug }] = useQueryParams(URL_CONFIG_MAP);
  const { data: organizationStructure } = useGetOrganizationStructure(organizationSlug);

  const locationType = useMemo(() => {
    if (!organizationStructure) return null;
    const searchLocationSlug = locationSlug ?? organizationSlug;

    const items = buildFlatStructure(organizationStructure);
    const currentLocation = items?.find(({ slug: currentLocationSlug }) => searchLocationSlug === currentLocationSlug);

    if (!currentLocation) return null;

    return currentLocation.type as GetOrganizationItemsLocationType;
  }, [locationSlug, organizationSlug, organizationStructure]);

  const { t } = useTranslation('organizations');
  const { data: organization } = useGetOrganizationBySlug(organizationSlug);

  usePageTitle(t('layouts.organizations.title'));

  const resolvedLocationSlug = locationSlug ?? organizationSlug;
  const resolvedLocationType = locationSlug ? locationType! : undefined;

  return (
    <LocationProvider
      organizationSlug={organizationSlug}
      locationSlug={resolvedLocationSlug}
      locationType={resolvedLocationType}
    >
      <OrganizationStructureProvider organizationSlug={organizationSlug}>
        <Box sx={{ px: 0, pb: 5, flexGrow: 1 }}>
          <StickyHead>
            <Stack direction="row" pt={2.25} gap={2} justifyContent="space-between" alignItems="start">
              <Stack>
                <Stack direction="row" alignItems="center" justifyContent="start">
                  <CachedLocationDisplay
                    rootLinkTo={toOrganizationItems({ organizationSlug })}
                    sx={{
                      '& .LocationItem-organization': {
                        marginInlineEnd: 0.5
                      },
                      '& .LocationItem-organization .LocationItem-icon': {
                        display: 'none'
                      },
                      '& .LocationItem-organization .LocationItem-label.MuiTypography-root': {
                        ...typography.h2,
                        color: 'text.primary',
                        whiteSpace: 'pre',
                        maxWidth: 'unset'
                      },
                      '& .LocationItem-label': {
                        ...typography.body2,
                        fontWeight: typography.fontWeightMedium
                      }
                    }}
                    organizationSlug={organizationSlug}
                    locationSlug={locationSlug || organizationSlug}
                    colorized
                    placeholder={
                      <Typography variant="h2" whiteSpace="pre">
                        {organization?.name}
                      </Typography>
                    }
                  />
                  <LocationNavSelect
                    organizationSlug={organizationSlug}
                    sx={{
                      '& .p-treeselect-trigger > .MuiBox-root': {
                        height: 24
                      },
                      '& .p-treeselect': {
                        marginInlineStart: -1
                      }
                    }}
                  />
                </Stack>
                <OrganizationInfo organizationSlug={organizationSlug} itemSlug={locationSlug || organizationSlug} />
              </Stack>
            </Stack>
            <OrganizationTabs />
          </StickyHead>
          <Outlet />
        </Box>
      </OrganizationStructureProvider>
    </LocationProvider>
  );
};

const OrganizationTabs = () => {
  const location = useLocation();
  const { organizationSlug } = useParams() as {
    organizationSlug: string;
  };
  const [{ locationSlug }] = useQueryParams(URL_CONFIG_MAP);
  const { location: locationData } = useOrganizationStructure(locationSlug);
  const { type: locationType } = locationData || {};

  const isProcessListEnabled = PROCESS_ENABLED_ITEM_TYPES.includes(locationType!);
  const isTalentListEnabled = TALENT_ENABLED_ITEM_TYPES.includes(locationType!);

  const theme = useTheme();
  const { spacing } = theme;
  const { t } = useTranslation('organizations');

  const pathMatcher = useMemo(
    () =>
      match(ROUTER_PATHS.ORGANIZATION_TAB_GENERIC, {
        end: false
      }),
    []
  );

  const getTabKey = useCallback(
    (url: string) => {
      if (url === '#') return null;
      const path = new URL(url, window.location.origin).pathname;
      const params = pathMatcher(path);

      if (!params) return null;

      const tabKey = params?.params?.tabKey;

      return tabKey;
    },
    [pathMatcher]
  );

  const { value: canViewBasicInfo } = useAnyPermissions({
    permissions: [
      PermissionKeys.OrganizationcanViewBasicInfo,
      PermissionKeys.SuborganizationcanViewBasicInfo,
      PermissionKeys.FoldercanViewBasicInfo,
      PermissionKeys.SubfoldercanViewBasicInfo
    ],
    variables: {
      organizationSlug,
      locationSlug,
      locationType
    }
  });

  const { value: canViewRolesAndUsers } = useAnyPermissions({
    permissions: [
      PermissionKeys.OrganizationcanViewRolesAndUsers,
      PermissionKeys.SuborganizationcanViewRolesAndUsers,
      PermissionKeys.FoldercanViewRolesAndUsers
    ],
    variables: { organizationSlug, locationSlug, locationType }
  });

  const { value: canViewProcessSettings } = useAnyPermissions({
    permissions: [
      PermissionKeys.OrganizationcanViewProcessSettings,
      PermissionKeys.SuborganizationcanViewProcessSettings
    ],
    variables: { organizationSlug, locationSlug, locationType }
  });
  const { value: canViewIdentificationAndNorms } = useAnyPermissions({
    permissions: [
      PermissionKeys.OrganizationcanViewIdentificationAndNorms,
      PermissionKeys.SuborganizationcanViewIdentificationAndNorms
    ],
    variables: { organizationSlug, locationSlug, locationType }
  });

  const { value: canViewOrganizationSettings } = useAnyPermissions({
    permissions: [
      PermissionKeys.OrganizationcanViewBasicInfo,
      PermissionKeys.OrganizationcanViewRolesAndUsers,
      PermissionKeys.OrganizationcanViewProcessSettings,
      PermissionKeys.OrganizationcanViewIdentificationAndNorms
    ],
    variables: { organizationSlug }
  });

  const { value: canViewFolderSettings } = useAnyPermissions({
    permissions: [PermissionKeys.FoldercanViewBasicInfo, PermissionKeys.FoldercanViewRolesAndUsers],
    variables: { organizationSlug, locationSlug, locationType }
  });

  const { value: { canViewBasicInfo: canViewSubfolderSettings } = {} } = useSubfolderPermissions({
    variables: { organizationSlug, locationSlug, locationType }
  });

  const { value: canViewSuborganizationSettings } = useAnyPermissions({
    permissions: [
      PermissionKeys.SuborganizationcanViewBasicInfo,
      PermissionKeys.SuborganizationcanViewRolesAndUsers,
      PermissionKeys.SuborganizationcanViewProcessSettings,
      PermissionKeys.SuborganizationcanViewIdentificationAndNorms
    ],
    variables: { organizationSlug, locationSlug, locationType }
  });

  const defaultStepKey = useMemo(() => {
    if (canViewBasicInfo) return SETTINGS_STEP_KEYS.BASIC_INFO;
    if (canViewRolesAndUsers) return SETTINGS_STEP_KEYS.ROLES_AND_MEMBERS;
    if (canViewProcessSettings) return SETTINGS_STEP_KEYS.PROCESS_SETTINGS;
    if (canViewIdentificationAndNorms) return SETTINGS_STEP_KEYS.IDENTIFICATION_AND_NORMS;

    return SETTINGS_STEP_KEYS.BASIC_INFO;
  }, [canViewProcessSettings, canViewIdentificationAndNorms, canViewBasicInfo, canViewRolesAndUsers]);

  const settingsTabRootLink = useMemo(() => {
    if (!locationData && locationSlug) return '#';
    if (!locationSlug) {
      return toOrganizationSettings({ organizationSlug, stepKey: defaultStepKey });
    }

    return toOrganizationItemSettings({
      organizationSlug,
      locationSlug,
      stepKey: defaultStepKey,
      locationType: locationData?.type as FolderType
    });
  }, [locationSlug, locationData, organizationSlug, defaultStepKey]);

  const settingsTabRootValue = useMemo(() => getTabKey(settingsTabRootLink), [getTabKey, settingsTabRootLink]);

  const itemsTabRootLink = useMemo(
    () =>
      toOrganizationItems({
        organizationSlug,
        locationSlug: locationSlug ?? ''
      }),
    [locationSlug, organizationSlug]
  );

  const itemsTabRootValue = useMemo(() => getTabKey(itemsTabRootLink), [getTabKey, itemsTabRootLink]);

  const processesTabRootLink = useMemo(
    () =>
      locationSlug
        ? toOrganizationProcesses({ organizationSlug, locationSlug })
        : toOrganizationProcesses({ organizationSlug }),
    [locationSlug, organizationSlug]
  );

  const processesTabRootValue = useMemo(() => getTabKey(processesTabRootLink), [getTabKey, processesTabRootLink]);

  const talentsTabRootLink = useMemo(
    () =>
      locationSlug
        ? toOrganizationTalents({ organizationSlug, locationSlug })
        : toOrganizationTalents({ organizationSlug }),
    [locationSlug, organizationSlug]
  );

  const talentsTabRootValue = useMemo(() => getTabKey(talentsTabRootLink), [getTabKey, talentsTabRootLink]);

  const currentTab = useMemo(() => getTabKey(location.pathname), [getTabKey, location.pathname]);

  const canViewLocationSettings = useMemo(() => {
    if (!locationSlug) {
      return canViewOrganizationSettings;
    }

    switch (locationType) {
      case FolderType.SUB_ORGANIZATION:
        return canViewSuborganizationSettings;
      case FolderType.FOLDER:
        return canViewFolderSettings;
      case FolderType.SUB_FOLDER:
        return canViewSubfolderSettings;
      default:
        return false;
    }
  }, [
    locationSlug,
    canViewOrganizationSettings,
    locationType,
    canViewSuborganizationSettings,
    canViewFolderSettings,
    canViewSubfolderSettings
  ]);

  return (
    <Stack mt={2}>
      <Tabs
        value={currentTab}
        sx={{
          minHeight: spacing(5),
          mt: 1.25,
          '& .MuiTab-root': {
            textTransform: 'none',
            borderTopRightRadius: spacing(0.5),
            borderTopLeftRadius: spacing(0.5),
            padding: spacing(1, 2),
            minHeight: spacing(5),
            color: 'text.primary',
            ...typography.body2
          },
          '& .MuiTabs-flexContainer': {
            borderBottom: borders.default,
            borderColor: 'grey.400'
          },
          '& .MuiTab-root.Mui-selected': {
            bgcolor: 'primary.100',
            color: 'primary.main',
            fontWeight: typography.fontWeightMedium
          }
        }}
      >
        <Tab
          label={t('layouts.organizations.details.tabs.items')}
          value={itemsTabRootValue}
          component={Link}
          to={itemsTabRootLink}
        />
        {isProcessListEnabled && (
          <Tab
            label={t('layouts.organizations.details.tabs.processes')}
            value={processesTabRootValue}
            component={Link}
            to={processesTabRootLink}
          />
        )}
        {isTalentListEnabled && (
          <Tab
            label={t('layouts.organizations.details.tabs.talents')}
            value={talentsTabRootValue}
            component={Link}
            to={talentsTabRootLink}
          />
        )}
        {canViewLocationSettings && (
          <Tab
            label={t('layouts.organizations.details.tabs.settings')}
            value={settingsTabRootValue}
            component={Link}
            to={settingsTabRootLink}
          />
        )}
      </Tabs>
    </Stack>
  );
};

export default Organization;
